<template>
  <div class="update-product">
    <KTCodePreview v-bind:title="''">
      <template v-slot:preview>
        <b-form ref="form" v-model="valid" lazy-validation>
          <b-card no-body class="full-width">
            <!-- Loading Overlay -->
            <b-overlay :show="loading" rounded="sm" opacity="0.6">
              <!-- Wizard Structure -->
              <product-wizard
                :current-step="currentStep"
                :show-combo="isComboProduct"
                :action="'update'"
                @step-changed="handleStepChange"
              >
                <!-- Step 1 Content -->
                <template v-slot:step-1>
                  <div class="col-md-12 full-width">
                    <product-information-section
                      v-model="productInfo"
                      :validation="$v.form"
                      :units="unitOptions"
                      :statuses="listStatus"
                      :categories="categories"
                      :filtered-categories="filteredCategories"
                      :brands="brands"
                      :filtered-brands="filteredBrands"
                      :product-types="listType"
                      :validation-state="validateState"
                      :filtered-options="filteredProducts"
                      @category-selected="handleCategorySelect"
                      @category-changed="handleCategoryChange"
                      @brand-selected="handleBrandSelect"
                      @brand-changed="handleBrandChange"
                      @parent-selected="handleParentSelect"
                      @parent-changed="handleParentChange"
                      @type-selected="handleTypeChange"
                      @generate-child="generateChildProducts"
                      @add-brand="showBrandModalFn"
                      @input-change="handleInputChange"
                    />

                    <!-- Product Options -->
                    <product-options-section
                      :options="productOptions"
                      @add-option="addNewOption"
                      @update:options="updateProductOptions"
                      ref="productOptions"
                    />

                    <div class="separator separator-dashed my-5"></div>

                    <!-- Child Products Table -->
                    <child-products-table
                      v-if="
                        isGenChild &&
                        !productInfo?.selectedParentId &&
                        productInfo?.selectedType !== 3
                      "
                      :products="childProducts"
                      :fields="fields"
                      @add-product="showAddProductModalFn"
                      @edit-product="handleEditChildProduct"
                      @delete-product="handleDeleteChildProduct"
                      :check-permission="checkPermission"
                    />
                  </div>
                </template>

                <!-- Step 2 Content (Store Pricing) -->
                <template v-slot:step-pricing>
                  <product-store-pricing-section
                    :active="currentStep === 2"
                    :product-id="Number(productId) || null"
                    :product-relation-type="productInfo?.productRelationType"
                  />
                </template>

                <!-- Step 3 Content (Combo) -->
                <template v-slot:step-2>
                  <product-combo-section
                    :active="currentStep === 3"
                    :products="comboProducts"
                    :fields="fieldTableCombo"
                    @add-product="handleAddComboProductClick"
                    @edit-product="handleEditComboProduct"
                    @update-product="handleUpdateComboProduct"
                    @delete-product="handleDeleteComboProduct"
                    :check-permission="checkPermission"
                  />
                </template>

                <!-- Step 4 Content (Images) -->
                <template v-slot:step-3>
                  <b-row>
                    <b-col class="p-8">
                      <upload-file
                        :model-id="productId"
                        :model-name="'products'"
                        :max-files="10"
                        :max-file-size="5 * 1024 * 1024"
                      />
                    </b-col>
                  </b-row>
                </template>

                <!-- Step 5 Content (Inventory) -->
                <template v-slot:step-4>
                  <product-inventory-section
                    :active="isInventoryStep"
                    :product-id="productId || null"
                    :product-code="productInfo?.code || ''"
                    :product-name="productInfo?.name || ''"
                  />
                </template>

                <!-- Step 6 Content (History) -->
                <template v-slot:step-5>
                  <product-history-section
                    :active="
                      currentStep === 6 ||
                      (currentStep === 5 && !isComboProduct)
                    "
                    :product-id="Number(productId) || null"
                  />
                </template>
              </product-wizard>
            </b-overlay>

            <!-- Modals -->
            <brand-modal
              :value="showBrandModal"
              :brandName.sync="brandName"
              @save="createBrand"
            />

            <child-product-modal
              ref="add-product-modal"
              v-model="showAddProduct"
              :product-options="productOptions"
              :initial-selected-options="selectedOptionValues"
              @option-selected="handleOptionSelected"
              :barcode.sync="childBarCode"
              :original-price.sync="childOriginalPrice"
              :sale-price.sync="childSalePrice"
              @save="createChildProduct"
              @cancel="hideAddProductModal"
            />

            <update-product-modal
              ref="update-product-modal"
              :barcode.sync="childBarCode"
              :code.sync="childCode"
              :name.sync="childName"
              :original-price.sync="childOriginalPrice"
              :sale-price.sync="childSalePrice"
              @save="updateChildProduct"
              @cancel="hideUpdateProductModal"
            />

            <product-combo-modal
              ref="add-product-combo-modal"
              :search-product.sync="searchProductForCombo"
              :quantity.sync="proComboQuantity"
              :combo-price.sync="productComboPrice"
              :combo-rate.sync="productComboRate"
              :filtered-options="filteredProducts"
              @select="handleComboProductSelect"
              @change="handleComboProductSearchChange"
              @save="addProductToCombo"
              @cancel="hideComboProductModal"
            />
          </b-card>
        </b-form>
      </template>

      <template v-slot:foot>
        <div class="col-md-12 full-width">
          <div class="row">
            <div class="col-md-3">
              <b-button
                style="font-weight: 600; width: 70px; margin-right: 10px"
                variant="primary"
                size="sm"
                @click="debounceUpdateProduct"
                :disabled="loading"
                v-if="checkPermission('PRODUCT_UPDATE')"
                >Lưu</b-button
              >
              <router-link to="/products" tag="button">
                <b-button
                  style="font-weight: 600; width: 70px"
                  variant="secondary"
                  size="sm"
                  >Hủy</b-button
                >
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { validationMixin } from 'vuelidate';
import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { getToastInstance } from '@/utils/toastHelper';
import { unMaskPrice } from '@/utils/common';
import KTCodePreview from '@/view/content/CodeTab.vue';
import KTUtil from '@/assets/js/components/util';
import KTWizard from '@/assets/js/components/wizard';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  UNIT_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  PRODUCT_STATUS_OPTIONS,
  PRODUCT_HEADER_FIELDS,
  COMBO_HEADER_FIELDS,
} from '@/utils/types';

// Components imports
import ProductWizard from './components/ProductWizard.vue';
import ProductInformationSection from './components/ProductInformationSection.vue';
import ProductOptionsSection from './components/ProductOptionsSection.vue';
import ChildProductsTable from './components/ChildProductsTable.vue';
import ProductStorePricingSection from './components/ProductStorePricingSection.vue';
import ProductComboSection from './components/ProductComboSection.vue';
import BrandModal from './components/modals/BrandModal.vue';
import ChildProductModal from './components/modals/ChildProductModal.vue';
import UpdateProductModal from './components/modals/UpdateProductModal.vue';
import ProductComboModal from './components/modals/ProductComboModal.vue';
import ProductHistorySection from './components/ProductHistorySection.vue';
import ProductInventorySection from './components/ProductInventorySection.vue';
import UploadFile from '@/view/modules/upload-v2/Upload.vue';

// Import service, actions and validators
import { ProductActions } from './productActions';
import { productValidators } from './validators/productValidators';

// Import store module and mutation helpers
import productModule from '@/core/services/store/modules/product.module';
import { PRODUCT_MUTATIONS } from '@/core/services/store/modules/mutationTypes';
import { createMutationCommitters } from '@/core/services/store/modules/mutationHelpers';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import permissionMixin from '@/view/mixins/permission.mixin';
import { PRODUCT_TYPE } from '@/utils/enum';

export default {
  name: 'UpdateProduct',
  mixins: [validationMixin, permissionMixin],
  components: {
    KTCodePreview,
    ProductWizard,
    ProductInformationSection,
    ProductOptionsSection,
    ChildProductsTable,
    ProductStorePricingSection,
    ProductComboSection,
    BrandModal,
    ChildProductModal,
    UpdateProductModal,
    ProductComboModal,
    UploadFile,
    ProductHistorySection,
    ProductInventorySection,
  },
  data() {
    return {
      productImages: [],

      currentStep: 1,
      valid: true,
      isGenChild: false,
      showBrandModal: false,
      showStatusModal: false,
      selectedOptionValues: {},
      showAddProduct: false,
      productId: null, // ID của sản phẩm đang cập nhật

      // Form data
      form: {
        name: '',
        code: '',
        barcode: '',
        category: '',
        listedPrice: 0,
        selectedCate: '',
        searchBrandName: '',
        unitId: null,
        productVat: null,
        warrantyMonthNo: null,
        productCodeVat: '',
        productNameVat: '',
        productBrandCode: '',
        alias: '',
        sku: '',
        note: '',
      },

      // Modal data
      brandName: '',
      childBarCode: '',
      childOriginalPrice: 0,
      childSalePrice: 0,
      childCode: '',
      childName: '',
      childId: 0,
      selectedColorChild: null,
      selectedSizeChild: null,
      searchProductForCombo: '',
      proComboQuantity: 1,
      productComboPrice: '',
      productComboRate: '',

      // Table configs
      fields: PRODUCT_HEADER_FIELDS,
      fieldTableCombo: COMBO_HEADER_FIELDS,

      // Static options
      listStatus: PRODUCT_STATUS_OPTIONS,
      listType: PRODUCT_TYPE_OPTIONS,
      unitOptions: UNIT_OPTIONS,

      // Property IDs
      productPropertyId: null,

      // Selected combo product
      selectedProductForCombo: {
        id: null,
        code: '',
        name: '',
        quantity: 0,
      },
    };
  },

  watch: {
    productInfo: {
      handler() {
        this.syncFormWithProductInfo();
      },
      deep: true,
    },
    serverErrors(newVal) {
      if (newVal) {
        makeToastFaile(newVal);
      }
    },
    '$route.query.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId && oldId !== undefined) {
          this.productId = newId;
          this.resetProductInfo();
          this.initData();
        }
      },
      immediate: false,
    },
  },

  computed: {
    // Map store state
    ...mapState({
      loading: (state) => state.product.loading,
      error: (state) => state.product?.error,
      categories: (state) => state.product.categories,
      filteredCategories: (state) => state.product.filteredCategories,
      brands: (state) => state.product.brands,
      filteredBrands: (state) => state.product.filteredBrands,
      colors: (state) => state.product.colors,
      sizes: (state) => state.product.sizes,
      productOptions: (state) => state.product.productOptions,
      childProducts: (state) => state.product.childProducts,
      comboProducts: (state) => state.product.comboProducts,
      filteredProducts: (state) => state.product.filteredProducts,
      serverErrors: (state) => state.product?.error,
    }),

    productInfo: {
      get() {
        return this.$store.getters['product/getProductInfo'] || {};
      },
      set(value) {
        this.$store.commit('product/UPDATE_PRODUCT_INFO', value);
      },
    },

    isComboProduct() {
      return this.$store.getters['product/getShowCombo'];
    },
    isUnitRequired() {
      return this.productInfo?.selectedType !== PRODUCT_TYPE.PRODUCT_SERVICE;
    },
    isVatRequired() {
      return this.productInfo?.selectedType !== PRODUCT_TYPE.PRODUCT_SERVICE;
    },
    isInventoryStep() {
      return this.isComboProduct
        ? this.currentStep === 5
        : this.currentStep === 4;
    },
  },

  created() {
    if (!this.$store.state.product) {
      this.$store.registerModule('product', productModule);
    }

    this.productId = this.$route.query.id;
    this.resetProductInfo();
    this.initData();

    this.mutationCommitters = createMutationCommitters(this.$store.commit);
  },

  mounted() {
    this.setupBreadcrumbs();
    this.initializeWizard();
  },

  beforeDestroy() {},

  methods: {
    ...getToastInstance(),

    ...mapActions({
      initProductData: 'product/initProductData',
      fetchProductById: 'product/fetchProductById',
      updateProductInfo: 'product/updateProductInfo',
      setProductOptions: 'product/setProductOptions',
      generateChildProductsAction: 'product/generateChildProducts',
      addChildProduct: 'product/addChildProduct',
      updateChildProductAction: 'product/updateChildProduct',
      removeChildProduct: 'product/removeChildProduct',
      addComboProduct: 'product/addComboProduct',
      updateComboProduct: 'product/updateComboProduct',
      removeComboProduct: 'product/removeComboProduct',
      toggleComboProductEdit: 'product/toggleComboProductEdit',
      calculateComboSum: 'product/calculateComboSum',
      updateProductAction: 'product/updateProduct',
      filterCategories: 'product/filterCategories',
      filterBrands: 'product/filterBrands',
      createBrandAction: 'product/createBrand',
      createPropertyValue: 'product/createPropertyValue',
      searchProducts: 'product/searchProducts',
      resetProductInfo: 'product/resetProductInfo',
    }),

    handleImageSubmit(imageIds) {
      this.updateProductInfo({ imageIds });
    },

    handleStepChange(step) {
      this.currentStep = step;

      setTimeout(() => {
        const wizard = document.querySelector('#kt_wizard_v4');
        if (wizard) {
          const steps = wizard.querySelectorAll('[data-wizard-type="step"]');
          steps.forEach((stepEl, index) => {
            if (index + 1 === step) {
              stepEl.setAttribute('data-wizard-state', 'current');
            } else {
              stepEl.removeAttribute('data-wizard-state');
            }
          });
        }
      }, 0);
    },

    goToStep(step) {
      if (this.wizard) {
        this.wizard.goTo(step);
      } else {
        this.currentStep = step;
      }
    },

    async initData() {
      await ProductActions.initializeProductEditor(this);
    },

    setupBreadcrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Sản phẩm', route: '/products' },
        { title: 'Danh sách sản phẩm', route: '/products' },
        { title: 'Cập nhật sản phẩm' },
      ]);
    },

    initializeWizard() {
      const wizard = new KTWizard('kt_wizard_v4', {
        startStep: 1,
        clickableSteps: true,
      });

      this.wizard = wizard;

      wizard.on('change', (wizardObj) => {
        this.currentStep = wizardObj.getStep();

        setTimeout(() => {
          KTUtil.scrollTop();
        }, 500);
      });
    },

    syncFormWithProductInfo() {
      if (this.productInfo) {
        this.form.name = this.productInfo.name || '';
        this.form.barcode = this.productInfo.barcode || '';
        this.form.code = this.productInfo.code || '';
        this.form.listedPrice = this.productInfo.listedPrice || 0;
        this.form.selectedCate = this.productInfo.selectedCate || '';
        this.form.searchBrandName = this.productInfo.searchBrandName || '';
        this.form.unitId = this.productInfo.unitId || null;
        this.form.productVat = this.productInfo.productVat || null;
        this.form.warrantyMonthNo = this.productInfo.warrantyMonthNo;
        this.form.productCodeVat = this.productInfo.productCodeVat || '';
        this.form.productNameVat = this.productInfo.productNameVat || '';
        this.form.productBrandCode = this.productInfo.productBrandCode || '';
        this.form.alias = this.productInfo.alias || '';
        this.form.sku = this.productInfo.sku || '';
        this.form.note = this.productInfo.note || '';
        this.productImages = this.productInfo.imageIds || [];

        this.isGenChild = false;
      }
    },

    handleInputChange(field, value) {
      this.form[field] = value;
      this.updateProductInfo({ [field]: value });
    },

    validateState(name) {
      const field = this.$v.form[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    validateStatePropColor(name) {
      const field = this.$v.color[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    validateStatePropStatus(name) {
      const field = this.$v.status[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    handleCategorySelect(event) {
      const categoryId = event.item.id;
      const categoryName = event.item.name;

      this.form.selectedCate = categoryName;
      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID}`,
        categoryId,
      );
      this.updateProductInfo({ selectedCate: categoryName });
    },

    handleCategoryChange(text) {
      this.form.selectedCate = text;
      this.updateProductInfo({ selectedCate: text });

      if (text === '') {
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_FILTERED_CATEGORIES}`,
          [...this.categories],
        );
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID}`,
          null,
        );
      } else {
        this.filterCategories(text);
      }
    },

    handleBrandSelect(event) {
      const brandId = event.item.id;
      const brandName = event.item.name;

      this.form.searchBrandName = brandName;
      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID}`,
        brandId,
      );
      this.updateProductInfo({ searchBrandName: brandName });
    },

    handleBrandChange(text) {
      this.form.searchBrandName = text;
      this.updateProductInfo({ searchBrandName: text });

      if (text === '') {
        this.$store.commit(`product/${PRODUCT_MUTATIONS.SET_FILTERED_BRANDS}`, [
          ...this.brands,
        ]);
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID}`,
          null,
        );
      } else {
        this.filterBrands(text);
      }
    },

    handleParentSelect(event) {
      const parentId = event.item.id;
      let parentName = event.item.name;

      if (event.item.code !== '') {
        parentName = event.item.code + ' - ' + event.item.name;
      }

      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID}`,
        parentId,
      );
      this.updateProductInfo({ selectedParent: parentName });
      this.generateChildProducts();
    },

    handleParentChange(text) {
      this.updateProductInfo({ selectedParent: text });
      this.searchProducts(text);
      this.generateChildProducts();

      if (!text || text.trim() === '') {
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID}`,
          null,
        );
      }
    },

    handleTypeChange() {
      this.generateChildProducts();
    },

    generateChildProducts() {
      this.generateChildProductsAction({ isGenChild: this.isGenChild });
    },

    // Modal methods
    showBrandModalFn() {
      this.showBrandModal = true;
      this.brandName = '';
    },

    async createBrand() {
      if (!this.brandName) {
        return;
      }

      try {
        const result = await this.createBrandAction({
          id: this.$uuid.v4(),
          name: this.brandName,
        });

        this.showBrandModal = false;

        if (result.status === 1) {
          makeToastSuccess(result.message);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Lỗi khi tạo thương hiệu: ' + error.message);
      }
    },

    async createPropertyStatus(statusName, statusCode) {
      if (!statusName || !statusCode) {
        return;
      }

      try {
        const result = await this.createPropertyValue({
          propertyData: {
            name: statusName,
            code: statusCode,
            productPropertyId: this.productPropertyId,
          },
          typeId: 2, // Status type
        });

        if (result.status === 1) {
          this.showStatusModal = false;
          makeToastSuccess(result.message);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Lỗi khi tạo thuộc tính: ' + error.message);
      }
    },

    showAddProductModalFn() {
      this.childBarCode = '';
      this.selectedColorChild = null;
      this.selectedSizeChild = null;
      this.childOriginalPrice = 0;
      this.childSalePrice = 0;
      this.showAddProduct = true;
    },

    hideAddProductModal() {
      this.showAddProduct = false;
    },

    handleEditChildProduct(item) {
      this.childId = item.id;
      this.childCode = item.productCode;
      this.childName = item.productName;
      this.childBarCode = item.barCode;
      this.childOriginalPrice = item.originalPrice;
      this.childSalePrice = item.sellingPrice;

      this.$refs['update-product-modal'].show();
    },

    hideUpdateProductModal() {
      this.$refs['update-product-modal'].hide();
    },

    handleAddComboProductClick() {
      this.searchProductForCombo = '';
      this.proComboQuantity = 1;
      this.productComboRate = '';
      this.productComboPrice = '';

      this.$refs['add-product-combo-modal'].show();
    },

    hideComboProductModal() {
      this.$refs['add-product-combo-modal'].hide();
    },

    handleDeleteChildProduct(item) {
      this.showDeleteConfirmation(item, (id) => {
        this.removeChildProduct(id);
      });
    },

    handleDeleteComboProduct(item) {
      this.showDeleteConfirmation(item, (id) => {
        this.removeComboProduct(id);
      });
    },

    showDeleteConfirmation(item, onConfirm) {
      this.$swal
        .fire({
          title: 'Xóa sản phẩm con!',
          text: 'Bạn có chắc muốn xóa sản phẩm con này không ?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'rgb(221, 51, 51)',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Xóa',
          cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value && onConfirm) {
            onConfirm(item.id);
          }
        });
    },

    handleComboProductSelect(event) {
      this.selectedProductForCombo = {
        id: event.item.id,
        name: event.item.name,
        code: event.item.code,
        quantity: 0,
      };
      this.searchProductForCombo = event.item.name;
    },

    handleComboProductSearchChange(text) {
      this.searchProductForCombo = text;
      this.searchProducts(text);
    },

    handleEditComboProduct(index) {
      this.toggleComboProductEdit(index);
    },

    handleUpdateComboProduct(index) {
      this.toggleComboProductEdit(index);
    },

    createChildProduct(data) {
      const optionValues = Object.values(data.selectedOptionsByName);
      const optionCodes = optionValues.join('-');
      const optionNames = optionValues.join(' - ');

      const product = {
        id: this.form.code + '-' + optionCodes,
        count:
          this.childProducts.length > 0
            ? this.childProducts[this.childProducts.length - 1].count + 1
            : 1,
        productName: this.form.name + (optionNames ? ' - ' + optionNames : ''),
        productCode: this.form.code + '-' + optionCodes,
        barCode: data.barcode,
        originalPrice: data.originalPrice ? unMaskPrice(data.originalPrice) : 0,
        sellingPrice: data.salePrice ? unMaskPrice(data.salePrice) : 0,
        totalQualityInstock: 0,
        options: data.selectedOptionsByName,
        status: this.productInfo.selectedStatus,
      };

      this.addChildProduct(product);
      this.hideAddProductModal();
    },

    updateChildProduct() {
      this.updateChildProductAction({
        id: this.childId,
        updates: {
          productCode: this.childCode,
          productName: this.childName,
          barCode: this.childBarCode,
          originalPrice: this.childOriginalPrice
            ? unMaskPrice(this.childOriginalPrice)
            : 0,
          sellingPrice: this.childSalePrice
            ? unMaskPrice(this.childSalePrice)
            : 0,
        },
      });

      this.hideUpdateProductModal();
    },

    // Combo product methods
    addProductToCombo() {
      const comboProduct = {
        id: this.selectedProductForCombo.id,
        productCode: this.selectedProductForCombo.code,
        productName: this.selectedProductForCombo.name,
        quantity: this.proComboQuantity || 1,
        productComboPrice: this.productComboPrice,
        productComboRate: this.productComboRate,
        isEdit: false,
      };

      // Check for duplicates
      const isDuplicate = this.comboProducts.some(
        (product) => product.id === comboProduct.id,
      );
      if (isDuplicate) {
        makeToastFaile('Sản phẩm trùng!');
        return;
      }

      this.addComboProduct(comboProduct);
      this.hideComboProductModal();
    },

    // Product options methods
    addNewOption() {
      if (this.$refs.productOptions) {
        this.$refs.productOptions.addNewOption();
      }
    },

    updateProductOptions(options) {
      this.setProductOptions(options);
    },

    handleOptionSelected({ optionId, value }) {
      this.$set(this.selectedOptionValues, optionId, value);
    },

    // Update product
    async updateProduct() {
      this.$v.form.$touch();
      const isValid = !this.$v.form.$invalid;

      if (!isValid) {
        return;
      }

      // Check required fields
      if (!this.$store.state.product.selectedCategoryId) {
        makeToastFaile('Vui lòng chọn danh mục sản phẩm');
        return;
      }

      // For combo products, check if sum matches selling price
      if (this.productInfo.selectedType === 3) {
        const sumComboPrice = await this.calculateComboSum();
        const sellingPrice = parseFloat(
          this.productInfo.salePrice.replace(/,/g, ''),
        );

        if (sumComboPrice !== sellingPrice) {
          makeToastFaile('Giá bán lẻ không bằng tổng giá sản phẩm combo!');
          return;
        }
      }

      try {
        const result = await this.updateProductAction({
          id: this.productId,
          form: this.form,
        });

        if (result.status === 1) {
          makeToastSuccess(result.message);
          setTimeout(() => {
            // Lấy query params từ sessionStorage để giữ lại filters
            const savedFilters = sessionStorage.getItem('listProductsFilters');
            const query = {};
            if (savedFilters) {
              try {
                const filters = JSON.parse(savedFilters);
                Object.assign(query, filters);
                // Xóa sessionStorage sau khi sử dụng
                sessionStorage.removeItem('listProductsFilters');
              } catch (e) {
                console.error('Error parsing saved filters:', e);
              }
            }
            this.$router.push({
              name: 'list-products',
              query: query,
            });
          }, TIME_TRIGGER);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Lỗi khi cập nhật sản phẩm: ' + error.message);
      }
    },

    debounceUpdateProduct: debounce(function () {
      this.updateProduct();
    }, TIME_TRIGGER),
  },

  validations: {
    form: {
      code: {
        required: (val) => !!val,
        maxLength: (val) => !val || val.length <= 50,
        minLength: (val) => !val || val.length >= 3,
        hasSpecialCharacters: productValidators.hasSpecialCharacters,
        checkAscent: productValidators.checkAscent,
      },
      name: {
        required: (val) => !!val,
      },
      selectedCate: {
        required: (val) => !!val,
      },
      // listedPrice: {
      //   required: (val) => !!val,
      //   checkCurrency: productValidators.checkCurrency,
      // },
      unitId: {
        required(val) {
          return this.isUnitRequired ? !!val : true;
        },
      },
      productVat: {
        required(val) {
          return this.isVatRequired ? val !== null && val !== undefined : true;
        },
      },
    },
    color: {
      colorCode: {
        required: (val) => !!val,
      },
      colorName: {
        required: (val) => !!val,
      },
    },
    status: {
      statusCode: {
        required: (val) => !!val,
      },
      statusName: {
        required: (val) => !!val,
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/sass/pages/wizard/wizard-4.scss';

.update-product {
  .input-group-append {
    cursor: pointer;

    &:hover i {
      color: #3699ff;
    }
  }

  .icon:hover {
    background-color: #90c6fc;
  }

  .card-header-tabs {
    margin-right: -21px !important;
    margin-left: -21px !important;
  }

  .full-width .nav-tabs .nav-item {
    margin-bottom: -1px;
    flex-grow: 1 !important;
    text-align: center !important;
  }

  input::-webkit-calendar-picker-indicator {
    display: none;
  }

  .label-header-form {
    font-size: 16px;
  }

  .form-group label {
    font-weight: 500;
  }
  // UI Feedback
  .form-group {
    margin-bottom: 0.75rem;
  }
  .form-group label {
    margin-bottom: 0.25rem;
  }
  .p {
    margin-bottom: 0.25rem;
  }

  .lable-sp {
    font-weight: 500;
  }

  .multi-select-wrapper,
  .auto-autosuggest-wrap-component {
    flex: 1 1 auto;
  }

  .border-grey {
    border-color: #e5eaee !important;
  }

  .overlay-spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
}
</style>
